import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { UserRights } from '@maximizer/core/shared/domain';
import { EntryType, PageTitle } from '@maximizer/outlook/shared/domain';
import { VerticalService } from './vertical.service';

@Injectable()
export class PageTitleService {
  private readonly translate = inject(TranslateService);
  private readonly verticalService = inject(VerticalService);

  mapPageTitle(
    title: PageTitle,
    activatedRoute: ActivatedRoute,
    userRights?: UserRights,
  ): string {
    switch (title) {
      case PageTitle.MailRead:
        return this.translate.instant('outlook.main.title');

      case PageTitle.EntryPage:
        return this.getEntryPageTitle(activatedRoute);

      case PageTitle.MailCompose:
        return this.translate.instant('outlook.compose.title');

      case PageTitle.CalendarCompose:
        return this.translate.instant('outlook.calendar.compose.title');

      case PageTitle.CalendarRead:
        return this.translate.instant('outlook.calendar.read.title');

      case PageTitle.RelatedOpportunitiesAndCases:
        return this.getRelatedOpportunitiesAndCasesTitle(userRights);

      case PageTitle.RelatedDetailOpportunitiesAndCases: {
        const entry = activatedRoute.snapshot.params['entry'];
        if (entry === EntryType.Opportunity) {
          return this.translate.instant('outlook.entries.opportunities');
        } else {
          return this.verticalService.translateVertical('cases');
        }
      }

      case PageTitle.Preferences:
        return this.translate.instant('outlook.preferences.title');

      case PageTitle.SyncConfiguration:
        return this.translate.instant('outlook.syncConfiguration.title');

      case PageTitle.AddLead:
        return this.translate.instant('outlook.lead.addTitle');

      case PageTitle.AddAddressBookEntry:
        return this.getAddAddressBookEntryTitle(activatedRoute);

      case PageTitle.EditAddressBookEntry:
        return this.getEditAddressBookEntryTitle(activatedRoute);

      case PageTitle.EditLead:
        return this.translate.instant('outlook.lead.editTitle');

      case PageTitle.AddAppointment:
        return this.translate.instant('outlook.appointment.addTitle');

      case PageTitle.AddCallLog:
        return this.translate.instant('outlook.callLog.addTitle');

      case PageTitle.AddOpportunity:
        return this.translate.instant('outlook.opportunity.addTitle', {
          label: this.translate.instant('outlook.entries.opportunity'),
        });

      case PageTitle.AddCase:
        return this.translate.instant('outlook.case.addTitle', {
          label: this.verticalService.translateVertical('case'),
        });

      case PageTitle.AddTask:
        return this.translate.instant('outlook.task.addTitle');

      case PageTitle.AddNote:
        return this.translate.instant('outlook.note.addTitle');

      case PageTitle.IqBoost:
        return this.translate.instant('outlook.iqBoost.title');

      default:
        return '';
    }
  }

  private getEntryPageTitle(activatedRoute: ActivatedRoute): string {
    const id = activatedRoute.snapshot.params['id'];
    const decoded = atob(id);
    const firstToken = decoded.split('\t')[0].toLowerCase();

    if (firstToken === EntryType.Individual) {
      return this.verticalService.translateVertical('individual');
    }

    return this.translate.instant(`outlook.entries.${firstToken}`);
  }

  private getRelatedOpportunitiesAndCasesTitle(
    userRights?: UserRights,
  ): string {
    if (!userRights?.opportunities.read) {
      return this.translate.instant('outlook.main.related.halfTitle', {
        label: this.verticalService.translateVertical('cases'),
      });
    }

    if (!userRights?.customerService.read) {
      return this.translate.instant('outlook.main.related.halfTitle', {
        label: this.translate.instant('outlook.entries.opportunities'),
      });
    }

    return this.translate.instant('outlook.main.related.fullTitle', {
      oppLabel: this.translate.instant('outlook.entries.opportunities'),
      caseLabel: this.verticalService.translateVertical('cases'),
    });
  }

  private getAddAddressBookEntryTitle(activatedRoute: ActivatedRoute): string {
    const addType = activatedRoute.snapshot.params['type'];

    if (addType === EntryType.Individual) {
      return this.translate.instant('outlook.main.actions.addIndividual', {
        label: this.verticalService.translateVertical('individual'),
      });
    }

    return this.translate.instant('outlook.main.actions.addContact');
  }

  private getEditAddressBookEntryTitle(activatedRoute: ActivatedRoute): string {
    const id = activatedRoute.snapshot.params['id'];
    const decoded = atob(id);
    const firstToken = decoded.split('\t')[0];

    if (firstToken.toLowerCase() === EntryType.Individual) {
      return this.translate.instant('outlook.abentry.editTitle', {
        type: this.verticalService.translateVertical('individual'),
      });
    }

    return this.translate.instant('outlook.abentry.editTitle', {
      type: this.translate.instant(firstToken),
    });
  }
}
